import { Component,  OnChanges, OnInit, OnDestroy, SimpleChanges, Output, EventEmitter, Input } from '@angular/core';
import { ActionSheetController } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { DataLoading } from '../../../models/DataLoading.interface';
import { TableField } from '../../../models/assets-data/receiving/ReceivingLogTableConfig.interface';
import { TableConfig } from '../../../models/table-config.interface';
import { User } from '../../../models/User.interface';
import { ScreenSizeService } from '../../../service/screen-size.service';
import { itemsHaveDamage } from '../../../domain/receiving-log/rules/damage-flags.rules';
/**
 * Table Component
 * ---------------
 * This component handles the display and interaction with the table of receiving logs. It supports pagination, sorting,
 * and opening modals for filtering and viewing/editing log details.
 *
 * Features:
 * - Displays logs in a table with pagination and sorting.
 * - Emits events for sorting columns, updating page numbers, opening row modals, opening filter modals, and navigation.
 * - Handles changes to input properties and updates the table accordingly.
 * - Provides methods for pagination, sorting, and opening modals.
 */

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.css'],
})
export class TableComponent implements OnChanges, OnInit, OnDestroy {
  @Input() isLargeScreen!: boolean; // Input property to determine if the screen is large
  @Input() dataLoading!: DataLoading; // Input property for data loading state
  @Input() dataLoadingMessage = ''; // Input property for data loading message

  @Input() tableConfig!: TableConfig; // Input property for table configuration
  @Input() columns: TableField[] = []; // Input property for table columns configuration
  @Input() items: any[] = []; // Input property for table items (logs)
  @Input() totalPages!: number; // Input property for total number of pages
  @Input() user!: User; // Input property for user
  @Input() isArchiveTable: boolean = false; // Input property to determine if this is an archive table
  @Output() sortByColumn = new EventEmitter<string>(); // Output event emitter for sorting by column
  @Output() sortColumnDirection = new EventEmitter<{ column: string; desc: boolean }>(); // Explicit column + direction (mobile sort sheet)
  @Output() updatePageNumber = new EventEmitter<number>(); // Output event emitter for updating page number
  @Output() openRowModal = new EventEmitter<any>(); // Output event emitter for opening row modal
  @Output() openFilterModal = new EventEmitter<void>(); // Output event emitter for opening filter modal
  @Output() navToPage = new EventEmitter<void>(); // Output event emitter for navigation
  @Output() deleteRow = new EventEmitter<any>(); // Output event emitter for deleting a row
  @Output() restoreRow = new EventEmitter<any>(); // Output event emitter for restoring an archived row
  @Output() generateEmail = new EventEmitter<any>(); // Output event emitter for generating an email
  @Output() reprintRow = new EventEmitter<any>(); // Output event emitter for reprinting a row's labels

  public currentPage: number = 1; // Current page number
  public selectedItem: any; // Selected item in the table
  public isModalOpen = false; // Flag to check if modal is open

  // True on phone-sized viewports (<= 768px). Drives the stacked card layout
  // in place of the wide scrolling table. Sourced from ScreenSizeService so the
  // shared table stays responsive without each parent having to pass it in.
  public isSmallScreen = false;
  private screenSizeSubscription?: Subscription;

  private _screenSizeService: ScreenSizeService;

  constructor(
    screenSizeService: ScreenSizeService,
    private _actionSheetCtrl: ActionSheetController,
  ) {
    this._screenSizeService = screenSizeService;
    this.isSmallScreen = this._screenSizeService.isSmallScreen.value;
  }

  ngOnInit(): void {
    this.screenSizeSubscription = this._screenSizeService.isSmallScreen.subscribe(
      (isSmallScreen: boolean) => {
        this.isSmallScreen = isSmallScreen;
      }
    );
  }

  ngOnDestroy(): void {
    this.screenSizeSubscription?.unsubscribe();
  }

  /**
   * Whether a row carries damaged items. Derived from the free-text `items`
   * string (damage is folded inline there — there is no boolean field on the
   * row). Non-receiving tables (e.g. laydown) have no `items` field, so this is
   * simply always false for them.
   */
  public rowHasDamage(item: any): boolean {
    return itemsHaveDamage(item?.items);
  }

  /**
   * The column used as a mobile card's headline. Config-driven via `cardTitle`;
   * falls back to the first column so any table still renders a sensible title.
   */
  public get cardTitleColumn(): TableField | undefined {
    return this.columns.find((c) => c.cardTitle) ?? this.columns[0];
  }

  /**
   * The secondary headline shown at the top-right of a mobile card (config via
   * `cardTitleRight`). Optional — undefined when no field opts in.
   */
  public get cardTitleRightColumn(): TableField | undefined {
    return this.columns.find((c) => c.cardTitleRight);
  }

  /**
   * The columns shown in a mobile card body. Kept deliberately short (config via
   * `showOnCard`) so cards stay scannable — the full record is one tap away in
   * the detail modal. Falls back to the first few non-title columns when a table
   * hasn't opted any fields in (e.g. laydown), never repeating a headline field.
   *
   * Short fields come first; long free-text (textarea) fields like Items sink to
   * the bottom of the card where their wrapping reads best.
   */
  public get cardColumns(): TableField[] {
    const headlineColumns = [this.cardTitleColumn, this.cardTitleRightColumn];
    const isHeadline = (c: TableField) => headlineColumns.includes(c);
    const opted = this.columns.filter((c) => c.showOnCard && !isHeadline(c));
    const chosen =
      opted.length > 0
        ? opted
        : this.columns.filter((c) => !isHeadline(c)).slice(0, 3);

    // Stable partition: keep config order within each group, textarea fields last.
    const short = chosen.filter((c) => c.formFieldType !== 'textarea');
    const long = chosen.filter((c) => c.formFieldType === 'textarea');
    return [...short, ...long];
  }


  /**
   * The columns a user can sort by on mobile — opted in per-column via the
   * config `sortable` flag (e.g. PO number, dates). Desktop keeps sorting by
   * any column via the header row, so this list is deliberately curated to the
   * handful worth a tap on a phone. Ordering itself is server-side, so what a
   * column sorts by (and how, e.g. PO trailing letters) is up to the API.
   */
  public get sortableColumns(): TableField[] {
    return this.columns.filter((c) => c.sortable);
  }

  /**
   * Mobile sort button: presents the sortable columns in an action sheet, each
   * offering both directions (newest/oldest for dates, highest/lowest for
   * numbers). The chosen column + direction is emitted explicitly so the parent
   * sets the order deterministically rather than the header-click toggle.
   */
  public async openSortSheet(): Promise<void> {
    const buttons: any[] = [];
    for (const col of this.sortableColumns) {
      const isDate = col.formFieldType === 'date';
      buttons.push({
        text: `${col.label} — ${isDate ? 'Newest First' : 'Highest First'}`,
        icon: 'chevron-down-outline',
        handler: () => this.sortColumnDirection.emit({ column: col.name, desc: true }),
      });
      buttons.push({
        text: `${col.label} — ${isDate ? 'Oldest First' : 'Lowest First'}`,
        icon: 'chevron-up-outline',
        handler: () => this.sortColumnDirection.emit({ column: col.name, desc: false }),
      });
    }
    buttons.push({ text: 'Cancel', role: 'cancel' });

    const sheet = await this._actionSheetCtrl.create({ header: 'Sort By', buttons });
    await sheet.present();
  }

  /**
   * Lifecycle hook that is called when any data-bound property of a directive changes.
   * Updates the component properties based on the changes.
   * @param changes - Object of SimpleChanges containing the changed properties
   */
  ngOnChanges(changes: SimpleChanges): void {
    if ('dataLoading' in changes) {
      this.dataLoading = changes['dataLoading'].currentValue;
    }

    if ('columns' in changes) {
      this.columns = changes['columns'].currentValue;
    }

    if ('items' in changes) {
      this.items = changes['items'].currentValue;
    }

    if ('totalPages' in changes) {
      this.totalPages = changes['totalPages'].currentValue;
    }

    if ('tableConfig' in changes) {
      this.tableConfig = changes['tableConfig'].currentValue;
    }
    if ('user' in changes) {
      this.user = changes['user'].currentValue;
    }
    if ('isArchiveTable' in changes) {
      this.isArchiveTable = changes['isArchiveTable'].currentValue;
    }
  }

  /**
   * Emits an event to the parent component to sort the table by the specified column.
   * @param columnName - The name of the column to sort by
   */
  public handleSortByColumn(columnName: string) {
    this.sortByColumn.emit(columnName);
  }

  /**
   * Emits an event to the parent component to navigate to a new page.
   */
  public handleNavToPage() {
    this.navToPage.emit();
  }

  /**
   * Navigates to the previous page if the current page is greater than 1.
   */
  prevPage() {
    if (this.currentPage > 1) {
      const newPage = this.currentPage - 1;
      this.handleUpdatePage(newPage);
    }
  }

  /**
   * Navigates to the next page if the current page is less than the total number of pages.
   */
  nextPage() {
    if (this.currentPage < this.totalPages) {
      const newPage = this.currentPage + 1;
      this.handleUpdatePage(newPage);
    }
  }

  /**
   * Updates the current page number and emits an event to the parent component to update the page number.
   * @param pageNumber - The new page number
   */
  public handleUpdatePage(pageNumber: number) {
    this.currentPage = pageNumber;
    this.updatePageNumber.emit(pageNumber);
  }

  /**
   * Emits an event to the parent component to open the filter modal.
   */
  public handleOpenFilterModal() {
    this.openFilterModal.emit();
  }


  /**
   * Emits an event to the parent component to open the row modal with the specified row.
   * @param item - The item to open in the row modal
   */
  handleOpenRowModule(item: any) {
    this.openRowModal.emit(item);
  }


  /**
   * Emits an event to the parent component to delete the specified row.
   * @param item - The item to delete
   */
  handleDeleteRow(item: any) {
    this.deleteRow.emit(item);
  }

  /**
   * Emits an event to the parent component to restore the specified archived row.
   * @param item - The archived item to restore
   */
  handleRestoreRow(item: any) {
    this.restoreRow.emit(item);
  }

  /**
   * Emits an event to the parent component to generate an email from the specified log entry.
   * @param item - The item to generate an email from
   */
  handleGenerateEmail(item: any) {
    this.generateEmail.emit(item);
  }

  /**
   * Emits an event to the parent component to reprint the specified log entry's
   * labels (opens the configure-print modal).
   * @param item - The log row to reprint labels for
   */
  handleReprintRow(item: any) {
    this.reprintRow.emit(item);
  }

  /**
   * Mobile card kebab (⋮) menu: presents the row's actions in an Ionic action
   * sheet. The same feature flags / role checks as the desktop icon row decide
   * which appear (email, reprint, admin delete or archive-restore). Each button
   * defers to the existing handler, so Delete keeps its current confirm flow.
   * "Details" isn't offered here — tapping the card already opens it.
   */
  public async openCardActions(item: any): Promise<void> {
    const buttons: any[] = [];

    if (this.tableConfig.hasEmailFeature) {
      buttons.push({
        text: this.tableConfig.buttons.generateEmailButton,
        icon: 'mail',
        handler: () => this.handleGenerateEmail(item),
      });
    }
    if (this.tableConfig.hasReprintFeature) {
      buttons.push({
        text: this.tableConfig.buttons.reprintLabelsButton,
        icon: 'print',
        handler: () => this.handleReprintRow(item),
      });
    }
    if (this.user.isAdmin && !this.isArchiveTable) {
      buttons.push({
        text: this.tableConfig.buttons.deleteEntryButton,
        icon: 'trash',
        role: 'destructive',
        handler: () => this.handleDeleteRow(item.id),
      });
    }
    if (this.user.isAdmin && this.isArchiveTable) {
      buttons.push({
        text: 'Restore Entry from Archive',
        icon: 'refresh-outline',
        handler: () => this.handleRestoreRow(item.id),
      });
    }
    buttons.push({ text: 'Cancel', role: 'cancel' });

    const titleColumn = this.cardTitleColumn;
    const header =
      titleColumn && item[titleColumn.name]
        ? `${titleColumn.label} ${item[titleColumn.name]}`
        : 'Log actions';

    const sheet = await this._actionSheetCtrl.create({ header, buttons });
    await sheet.present();
  }
}